// ============================================================================
// DebugDrawer — Admin Shell-Level Diagnostic Tool
// ----------------------------------------------------------------------------
// RULE: This component is rendered ONCE inside AdminLayout.
// Do NOT import or mount it in individual pages. All admin pages automatically
// get this drawer via the shared layout.
//
// Owner-only visibility: checks role === 'owner' via useAdminCheck.
// All sensitive data is redacted before display.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdminCheck } from '@/hooks/useAdminCheck';
import { supabase } from '@/services/supabaseClient';
import { DISPLAY_TAGS } from '@/modules/product/types';
import { getIdentityGatewayDiagnostics } from '@/services/identityGatewayService';
import type { IdentityGatewayDiagnostic } from '@/services/identityGatewayService';
import { getContractDiagnostic } from '@/services/contractDiagnosticStore';
import type { DisplayTagId } from '@/modules/product/types';
import type { TrilingualFieldStatus } from '@/modules/trilingual/capabilities';

// -----------------------------------------------
// Types
// -----------------------------------------------
export interface DebugDataSource {
  type: 'mock' | 'config' | 'api' | 'supabase' | 'edge-function';
  label?: string;
}

export interface DebugTagInfo {
  raw: string[];
  normalized: string[];
}

export interface DebugTrilingualField {
  field: string;
  /** zh value present */
  zh: boolean;
  /** ja value present */
  ja: boolean;
  /** en value present */
  en: boolean;
  /** payload status: enabled / omitted / disabled */
  enPayloadStatus: TrilingualFieldStatus;
}

export interface DebugI18nInfo {
  requestedLocale: string;
  displayedLocale: string;
  fallbackChain: string[];
  missingEnFields?: string[];
  missingJaFields?: string[];
  missingZhFields?: string[];
  /** Tri-lingual field-level detail (for admin pages) */
  trilingualFields?: DebugTrilingualField[];
}

export interface DebugPageData {
  /** Data source info */
  dataSource?: DebugDataSource;
  /** Loading state */
  loading?: boolean;
  /** Error state */
  error?: string | null;
  /** Whether data is empty */
  isEmpty?: boolean;
  /** Response summary (any printable object) */
  responseSummary?: Record<string, unknown> | null;
  /** i18n info */
  i18n?: DebugI18nInfo;
  /** Tag debug info */
  tags?: DebugTagInfo;
}

interface Props {
  /** @deprecated DebugDrawer is a shell-level tool. Pass pageData only for temporary deep diagnostics; prefer updating the service-layer diagnostic capture instead. */
  pageData?: DebugPageData;
}

// -----------------------------------------------
// Auth debug types
// -----------------------------------------------
interface CapturedError {
  id: string;
  type: 'window_error' | 'unhandled_rejection' | 'auth_api_error' | 'edge_function' | 'unknown';
  message: string;
  timestamp: string;
  stack?: string;
  details?: string;
}

interface SessionInfo {
  hasSession: boolean;
  userEmail: string | null;
  userIdPrefix: string | null;
  accessTokenExists: boolean;
  refreshTokenExists: boolean;
  expiresAt: number | null;
  expiresIn: number | null;
  tokenExpired: boolean;
}

// -----------------------------------------------
// Helper: check tag validity
// -----------------------------------------------
const CANONICAL_KEYS: DisplayTagId[] = DISPLAY_TAGS.map((d) => d.id);
const LEGACY_VALUES = [
  'coming_soon', 'crew_package', 'coming', 'crew_required',
  '熱門', '新上架', '新品', '預計進貨', '即將上架', '需搭人員', '需搭工作人員', '出班組限定',
];

function analyzeTag(raw: string): { canonical: boolean; legacy: boolean; label: string } {
  const isCanonical = CANONICAL_KEYS.includes(raw as DisplayTagId);
  const isLegacy = LEGACY_VALUES.includes(raw);
  const tag = DISPLAY_TAGS.find((d) => d.id === (raw as DisplayTagId));
  return {
    canonical: isCanonical,
    legacy: isLegacy,
    label: tag?.name_zh ?? raw,
  };
}

// -----------------------------------------------
// Helpers: redaction & environment
// -----------------------------------------------
function getHostOnly(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url || '—';
  }
}

function getEnvLabel(): string {
  const hostname = window.location.hostname;
  if (hostname.includes('readdy') || hostname.includes('preview')) return 'preview';
  if (hostname.includes('staging')) return 'staging';
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) return 'local';
  return 'production';
}

function redactSensitiveData(obj: unknown): unknown {
  if (typeof obj === 'string') {
    let s = obj;
    s = s.replace(/access_token["']?\s*[:=]\s*["'][^"']+["']/gi, 'access_token: [REDACTED]');
    s = s.replace(/refresh_token["']?\s*[:=]\s*["'][^"']+["']/gi, 'refresh_token: [REDACTED]');
    s = s.replace(/Authorization["']?\s*[:=]\s*["'][^"']+["']/gi, 'Authorization: [REDACTED]');
    s = s.replace(/apikey["']?\s*[:=]\s*["'][^"']+["']/gi, 'apikey: [REDACTED]');
    s = s.replace(/service_role["']?\s*[:=]\s*["'][^"']+["']/gi, 'service_role: [REDACTED]');
    s = s.replace(/Bearer\s+[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+\.[A-Za-z0-9\-_]+/g, 'Bearer [REDACTED_JWT]');
    return s;
  }
  if (Array.isArray(obj)) {
    return obj.map(redactSensitiveData);
  }
  if (obj && typeof obj === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      if (['access_token', 'refresh_token', 'apikey', 'service_role', 'authorization'].includes(lowerKey)) {
        result[key] = '[REDACTED]';
      } else {
        result[key] = redactSensitiveData(value);
      }
    }
    return result;
  }
  return obj;
}

// -----------------------------------------------
// Section component
// -----------------------------------------------
function Section({ title, icon, children }: { title: string; icon: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
        <i className={`${icon} text-sm`}></i>
        {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, mono = false, badge, color }: {
  label: string;
  value: string | number | boolean | null | undefined;
  mono?: boolean;
  badge?: boolean;
  color?: 'green' | 'red' | 'amber' | 'gray';
}) {
  const colorMap = {
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    amber: 'bg-amber-100 text-amber-700',
    gray: 'bg-gray-100 text-gray-600',
  };
  const displayVal = value === null || value === undefined ? '—' : String(value);
  return (
    <div className="flex items-start justify-between gap-2 text-xs py-0.5">
      <span className="text-gray-500 shrink-0">{label}</span>
      {badge ? (
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colorMap[color ?? 'gray']}`}>
          {displayVal}
        </span>
      ) : (
        <span className={`text-right break-all ${mono ? 'font-mono text-gray-700' : 'text-gray-800'}`}>
          {displayVal}
        </span>
      )}
    </div>
  );
}

// -----------------------------------------------
// Tri-lingual field row component
// -----------------------------------------------
function TrilingualFieldRow({ field }: { field: DebugTrilingualField }) {
  const statusColor: Record<TrilingualFieldStatus, string> = {
    enabled: 'bg-green-100 text-green-700',
    omitted: 'bg-amber-100 text-amber-700',
    disabled: 'bg-red-100 text-red-700',
  };

  return (
    <div className="flex items-center justify-between gap-1 py-0.5 text-xs">
      <span className="font-mono text-gray-600 shrink-0">{field.field}</span>
      <div className="flex items-center gap-1">
        <span className={`w-4 text-center rounded ${field.zh ? 'text-green-700 font-bold' : 'text-gray-300'}`}>zh</span>
        <span className={`w-4 text-center rounded ${field.ja ? 'text-green-700 font-bold' : 'text-gray-300'}`}>ja</span>
        <span className={`w-4 text-center rounded ${field.en ? 'text-green-700 font-bold' : 'text-gray-300'}`}>en</span>
        <span className={`ml-1 px-1.5 py-0.5 rounded text-xs font-medium ${statusColor[field.enPayloadStatus]}`}>
          {field.enPayloadStatus}
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------
// Identity Gateway Diagnostic Row
// -----------------------------------------------
function GatewayDiagnosticRow({ diag }: { diag: IdentityGatewayDiagnostic }) {
  const errorTypeLabel: Record<string, string> = {
    cors: 'CORS',
    network: 'Network',
    auth: 'Auth',
    gateway_4xx: '4xx',
    gateway_5xx: '5xx',
    unknown: 'Err',
  };
  const errorTypeColor: Record<string, string> = {
    cors: 'bg-red-100 text-red-700',
    network: 'bg-red-100 text-red-700',
    auth: 'bg-amber-100 text-amber-700',
    gateway_4xx: 'bg-orange-100 text-orange-700',
    gateway_5xx: 'bg-red-100 text-red-700',
    unknown: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="mb-2 p-2 bg-white rounded border border-gray-100">
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-2 h-2 rounded-full shrink-0 ${diag.status === 'success' ? 'bg-green-500' : 'bg-red-500'}`}></span>
        <span className="font-mono text-gray-700 text-xs">{diag.method} {diag.path}</span>
        {diag.errorType && diag.errorType !== 'unknown' && (
          <span className={`px-1 py-0.5 rounded text-[10px] font-medium ml-1 ${errorTypeColor[diag.errorType] || errorTypeColor.unknown}`}>
            {errorTypeLabel[diag.errorType] || diag.errorType}
          </span>
        )}
        <span className="text-gray-400 ml-auto text-[10px]">{new Date(diag.timestamp).toLocaleTimeString()}</span>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500">
        {diag.statusCode !== undefined && <span>HTTP {diag.statusCode}</span>}
        {diag.requestOrigin && <span>origin: {diag.requestOrigin}</span>}
        {diag.customerId && <span>customer: {diag.customerId.slice(0, 8)}...</span>}
        {diag.orderId && <span>order: {diag.orderId.slice(0, 8)}...</span>}
      </div>
      {diag.errorMessage && (
        <div className="mt-1 text-red-600 text-xs break-all">{diag.errorMessage}</div>
      )}
      {diag.extra && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-gray-500 mt-0.5">
          {diag.extra.frontUrlExists !== undefined && (
            <span>frontUrl: {diag.extra.frontUrlExists ? 'yes' : 'no'}</span>
          )}
          {diag.extra.backUrlExists !== undefined && (
            <span>backUrl: {diag.extra.backUrlExists ? 'yes' : 'no'}</span>
          )}
          {diag.extra.responseKeys && Array.isArray(diag.extra.responseKeys) && (
            <span>keys: {(diag.extra.responseKeys as string[]).join(', ')}</span>
          )}
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------
// Main component
// -----------------------------------------------
export default function DebugDrawer({ pageData }: Props) {
  const { role, checking } = useAdminCheck();
  const location = useLocation();
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);

  // --- Auth / session state (owner only) ---
  const [sessionInfo, setSessionInfo] = useState<SessionInfo>({
    hasSession: false,
    userEmail: null,
    userIdPrefix: null,
    accessTokenExists: false,
    refreshTokenExists: false,
    expiresAt: null,
    expiresIn: null,
    tokenExpired: false,
  });

  const [capturedErrors, setCapturedErrors] = useState<CapturedError[]>([]);

  // --- Visibility gating: owner only ---
  useEffect(() => {
    if (checking) return;
    setVisible(role === 'owner');
  }, [role, checking]);

  // --- Sync session ---
  const syncSession = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      setSessionInfo({
        hasSession: !!session,
        userEmail: session?.user?.email ?? null,
        userIdPrefix: session?.user?.id ? `${session.user.id.slice(0, 8)}...` : null,
        accessTokenExists: !!session?.access_token,
        refreshTokenExists: !!session?.refresh_token,
        expiresAt: session?.expires_at ?? null,
        expiresIn: session?.expires_in ?? null,
        tokenExpired: session?.expires_at ? Date.now() / 1000 > session.expires_at : false,
      });
    } catch {
      // ignore sync errors
    }
  }, []);

  useEffect(() => {
    syncSession();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSessionInfo({
        hasSession: !!session,
        userEmail: session?.user?.email ?? null,
        userIdPrefix: session?.user?.id ? `${session.user.id.slice(0, 8)}...` : null,
        accessTokenExists: !!session?.access_token,
        refreshTokenExists: !!session?.refresh_token,
        expiresAt: session?.expires_at ?? null,
        expiresIn: session?.expires_in ?? null,
        tokenExpired: session?.expires_at ? Date.now() / 1000 > session.expires_at : false,
      });
    });
    return () => subscription.unsubscribe();
  }, [syncSession]);

  // --- Token expiration ticker ---
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionInfo((prev) => ({
        ...prev,
        tokenExpired: prev.expiresAt ? Date.now() / 1000 > prev.expiresAt : false,
      }));
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // --- Error capture ---
  const addError = useCallback((err: Omit<CapturedError, 'id' | 'timestamp'>) => {
    setCapturedErrors((prev) => {
      const next = [
        {
          ...err,
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          timestamp: new Date().toISOString(),
        },
        ...prev,
      ];
      return next.slice(0, 5);
    });
  }, []);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      addError({
        type: 'window_error',
        message: redactSensitiveData(String(event.message)) as string,
        stack: event.error?.stack ? String(event.error.stack) : undefined,
      });
    };
    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      let type: CapturedError['type'] = 'unhandled_rejection';
      const rawMessage = String(reason?.message ?? reason);
      const message = redactSensitiveData(rawMessage) as string;

      if (
        rawMessage.includes('Invalid Refresh Token') ||
        rawMessage.includes('Refresh Token Not Found') ||
        rawMessage.includes('invalid_grant')
      ) {
        type = 'auth_api_error';
      } else if (
        rawMessage.includes('Edge Function') ||
        rawMessage.includes('non-2xx') ||
        rawMessage.includes('Function invocation')
      ) {
        type = 'edge_function';
      }

      let details: string | undefined;
      if (reason && typeof reason === 'object') {
        const d = { ...reason };
        delete (d as Record<string, unknown>).message;
        delete (d as Record<string, unknown>).stack;
        if (Object.keys(d).length > 0) {
          details = JSON.stringify(redactSensitiveData(d));
        }
      }

      addError({ type, message, stack: reason?.stack ? String(reason.stack) : undefined, details });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [addError]);

  // --- Derived flags ---
  const hasRefreshTokenError = capturedErrors.some(
    (e) =>
      e.type === 'auth_api_error' &&
      (e.message.includes('Invalid Refresh Token') || e.message.includes('Refresh Token Not Found'))
  );

  // --- Identity Gateway diagnostics (read from module-level store) ---
  const gatewayDiagnostics = getIdentityGatewayDiagnostics();

  // Count CORS errors for quick visibility
  const corsErrorCount = gatewayDiagnostics.filter(d => d.errorType === 'cors').length;

  // --- Actions ---
  const handleRefreshSession = async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        addError({ type: 'auth_api_error', message: `Refresh failed: ${error.message}` });
      } else {
        addError({ type: 'unknown', message: `Session refreshed. hasSession=${!!data.session}` });
        await syncSession();
      }
    } catch (e) {
      addError({ type: 'auth_api_error', message: `Refresh exception: ${String(e)}` });
    }
  };

  const handleClearAuthStorage = () => {
    try {
      const keysToRemove = Object.keys(localStorage).filter(
        (k) => k.startsWith('supabase') || k.includes('sb-') || k.includes('auth')
      );
      keysToRemove.forEach((k) => localStorage.removeItem(k));
      addError({ type: 'unknown', message: 'Local auth storage cleared. Reloading in 1s...' });
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) {
      addError({ type: 'unknown', message: `Clear failed: ${String(e)}` });
    }
  };

  const handleSignOutAndReload = async () => {
    try {
      await supabase.auth.signOut({ scope: 'global' });
    } catch {
      // ignore
    }
    window.location.reload();
  };

  const generateDebugReport = useCallback((): string => {
    const report = {
      timestamp: new Date().toISOString(),
      environment: {
        route: location.pathname,
        version: import.meta.env.VITE_APP_VERSION ?? '—',
        supabaseHost: getHostOnly(import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? ''),
        apiBaseHost: getHostOnly((import.meta.env.VITE_API_BASE_URL as string) ?? ''),
        identityGatewayHost: getHostOnly((import.meta.env.VITE_IDENTITY_GATEWAY_BASE_URL as string) ?? ''),
        envLabel: getEnvLabel(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '—',
      },
      auth: {
        hasSession: sessionInfo.hasSession,
        userEmail: sessionInfo.userEmail,
        userIdPrefix: sessionInfo.userIdPrefix,
        accessTokenExists: sessionInfo.accessTokenExists,
        refreshTokenExists: sessionInfo.refreshTokenExists,
        expiresAt: sessionInfo.expiresAt,
        tokenExpired: sessionInfo.tokenExpired,
      },
      capturedErrors: capturedErrors.map((e) => ({
        type: e.type,
        message: e.message,
        timestamp: e.timestamp,
        details: e.details,
      })),
      identityGatewayDiagnostics: gatewayDiagnostics.map((d) => ({
        method: d.method,
        path: d.path,
        status: d.status,
        statusCode: d.statusCode,
        errorType: d.errorType,
        requestOrigin: d.requestOrigin,
        customerId: d.customerId ? `${d.customerId.slice(0, 8)}...` : undefined,
        orderId: d.orderId ? `${d.orderId.slice(0, 8)}...` : undefined,
        errorMessage: d.errorMessage,
        timestamp: d.timestamp,
      })),
      contractDiagnostic: getContractDiagnostic() ? {
        orderId: getContractDiagnostic()?.orderId ? `${getContractDiagnostic()?.orderId?.slice(0, 8)}...` : undefined,
        contractMode: getContractDiagnostic()?.contractMode,
        snapshotExists: getContractDiagnostic()?.snapshotExists,
        signatureExists: getContractDiagnostic()?.signatureExists,
        templateVersion: getContractDiagnostic()?.templateVersion,
      } : null,
      pageData: pageData ? redactSensitiveData(pageData) : null,
    };
    return JSON.stringify(report, null, 2);
  }, [location.pathname, sessionInfo, capturedErrors, pageData, gatewayDiagnostics]);

  const handleCopyReport = async () => {
    try {
      await navigator.clipboard.writeText(generateDebugReport());
      addError({ type: 'unknown', message: 'Debug report copied to clipboard' });
    } catch (e) {
      addError({ type: 'unknown', message: `Copy failed: ${String(e)}` });
    }
  };

  // -----------------------------------------------
  // Computed tag analysis
  // -----------------------------------------------
  const rawTags = pageData?.tags?.raw ?? [];
  const normalizedTags = pageData?.tags?.normalized ?? [];
  const hasLegacy = rawTags.some((t) => LEGACY_VALUES.includes(t));
  const hasIllegal = rawTags.some((t) => !CANONICAL_KEYS.includes(t as DisplayTagId) && !LEGACY_VALUES.includes(t));

  if (!visible) return null;

  return (
    <>
      {/* Toggle FAB */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-[9999] w-10 h-10 flex items-center justify-center rounded-full bg-gray-900 text-white cursor-pointer"
        title="Owner Debug"
        style={{ opacity: 0.85 }}
      >
        <i className={`${open ? 'ri-close-line' : 'ri-bug-line'} text-lg`}></i>
      </button>

      {/* Drawer panel */}
      {open && (
        <div className="fixed bottom-20 right-6 z-[9998] w-80 max-h-[70vh] overflow-y-auto bg-white border border-gray-200 rounded-xl p-4 text-xs">
          <div className="flex items-center justify-between mb-3">
            <span className="font-bold text-sm text-gray-900 flex items-center gap-1.5">
              <i className="ri-bug-2-line text-amber-500"></i>
              Owner Debug
            </span>
            <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium">
              owner only
            </span>
          </div>

          {/* ── OWNER AUTH DEBUG ── */}
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-amber-800 uppercase tracking-wider">
              <i className="ri-shield-keyhole-line text-sm"></i>
              Auth Debug
            </div>

            {/* Invalid Refresh Token Warning */}
            {hasRefreshTokenError && (
              <div className="mb-2 p-2 bg-red-100 text-red-700 rounded text-xs">
                <div className="font-semibold mb-0.5">Refresh Token 失效</div>
                偵測到 refresh token 失效，請按「Clear local Supabase auth storage」後重新登入。
              </div>
            )}

            {/* Environment */}
            <Section title="Environment" icon="ri-earth-line">
              <Row label="Route" value={location.pathname} mono />
              <Row label="Version" value={import.meta.env.VITE_APP_VERSION ?? '—'} mono />
              <Row label="Request origin" value={typeof window !== 'undefined' ? window.location.origin : '—'} mono />
              <Row
                label="Supabase host"
                value={getHostOnly(import.meta.env.VITE_PUBLIC_SUPABASE_URL ?? '')}
                mono
              />
              <Row
                label="API host"
                value={getHostOnly((import.meta.env.VITE_API_BASE_URL as string) ?? '')}
                mono
              />
              <Row
                label="Identity GW"
                value={getHostOnly((import.meta.env.VITE_IDENTITY_GATEWAY_BASE_URL as string) ?? '')}
                mono
                badge
                color={import.meta.env.VITE_IDENTITY_GATEWAY_BASE_URL ? 'green' : 'red'}
              />
              <Row
                label="Env label"
                value={getEnvLabel()}
                badge
                color={getEnvLabel() === 'production' ? 'green' : 'amber'}
              />
            </Section>

            {/* Session State */}
            <Section title="Session" icon="ri-key-line">
              <Row
                label="hasSession"
                value={sessionInfo.hasSession}
                badge
                color={sessionInfo.hasSession ? 'green' : 'red'}
              />
              <Row label="User email" value={sessionInfo.userEmail} />
              <Row label="User ID" value={sessionInfo.userIdPrefix} mono />
              <Row
                label="access_token"
                value={sessionInfo.accessTokenExists ? 'exists' : 'missing'}
                badge
                color={sessionInfo.accessTokenExists ? 'green' : 'red'}
              />
              <Row
                label="refresh_token"
                value={sessionInfo.refreshTokenExists ? 'exists' : 'missing'}
                badge
                color={sessionInfo.refreshTokenExists ? 'green' : 'red'}
              />
              <Row
                label="expires_at"
                value={
                  sessionInfo.expiresAt
                    ? new Date(sessionInfo.expiresAt * 1000).toLocaleString()
                    : '—'
                }
                mono
              />
              <Row label="expires_in" value={sessionInfo.expiresIn ?? '—'} mono />
              <Row
                label="token_expired"
                value={sessionInfo.tokenExpired}
                badge
                color={sessionInfo.tokenExpired ? 'red' : 'green'}
              />
            </Section>

            {/* Captured Errors */}
            <Section title="Recent Errors" icon="ri-error-warning-line">
              {capturedErrors.length === 0 ? (
                <div className="text-gray-400 italic">No captured errors</div>
              ) : (
                capturedErrors.map((err) => (
                  <div key={err.id} className="mb-2 p-2 bg-white rounded border border-gray-100">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          err.type === 'auth_api_error'
                            ? 'bg-red-500'
                            : err.type === 'edge_function'
                              ? 'bg-amber-500'
                              : 'bg-gray-400'
                        }`}
                      ></span>
                      <span className="font-medium text-gray-700">{err.type}</span>
                      <span className="text-gray-400 ml-auto">
                        {new Date(err.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-gray-600 break-all">{err.message}</div>
                    {err.details && (
                      <div className="mt-1 text-gray-400 break-all">{err.details}</div>
                    )}
                  </div>
                ))
              )}
            </Section>

            {/* Identity Gateway Diagnostics */}
            <Section title="Identity Gateway" icon="ri-server-line">
              {corsErrorCount > 0 && (
                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                  <div className="font-semibold mb-0.5">CORS / Network Error Detected</div>
                  <div>Failed to fetch from Railway gateway. Check Access-Control-Allow-Origin on server.</div>
                  <div className="mt-1 text-[10px] text-red-500">Errors: {corsErrorCount} · Origin: {typeof window !== 'undefined' ? window.location.origin : '—'}</div>
                </div>
              )}
              {gatewayDiagnostics.length === 0 ? (
                <div className="text-gray-400 italic">no diagnostic captured</div>
              ) : (
                gatewayDiagnostics.map((diag) => (
                  <GatewayDiagnosticRow key={`${diag.timestamp}-${diag.path}`} diag={diag} />
                ))
              )}
            </Section>

            {/* Emergency Print Diagnostics — always shown on /admin/orders routes */}
      {location.pathname.startsWith('/admin/orders') && (
        <Section title="Emergency Print" icon="ri-printer-line">
          <Row label="emergencyPrintUiEnabled" value="true" badge color="green" />
          <Row
            label="selectedEmergencyPrintType"
            value={(() => {
              const m = location.pathname.match(/emergency-print\/([a-z\-]+)/);
              return m ? m[1] : '—';
            })()}
            mono
          />
          <Row label="printSerialBackendReady" value="false" badge color="amber" />
          <Row label="auditBackendReady" value="false" badge color="amber" />
          <Row label="paperContractFallback" value="staging" badge color="amber" />
        </Section>
      )}

      {/* Contract Diagnostics */}
            {(() => {
              const cd = getContractDiagnostic();
              if (!cd) return null;
              return (
                <Section title="Contract" icon="ri-file-paper-line">
                  <Row label="contractMode" value={cd.contractMode ?? '—'} badge color={cd.contractMode === 'snapshot' ? 'green' : cd.contractMode === 'ipad_sign' ? 'gray' : 'gray'} />
                  <Row label="snapshotExists" value={cd.snapshotExists ?? false} badge color={cd.snapshotExists ? 'green' : 'amber'} />
                  <Row label="signatureExists" value={cd.signatureExists ?? false} badge color={cd.signatureExists ? 'green' : 'gray'} />
                  <Row label="templateVersion" value={cd.templateVersion ?? '—'} mono />
                  <Row label="templateId" value={cd.templateId ? `${cd.templateId.slice(0, 8)}...` : '—'} mono />
                  {cd.orderId && <Row label="orderId" value={`${cd.orderId.slice(0, 8)}...`} mono />}
                  {cd.identityGateOpen !== undefined && (
                    <Row label="identityGateOpen" value={cd.identityGateOpen} badge color={cd.identityGateOpen ? 'red' : 'green'} />
                  )}
                  {cd.signDate && <Row label="signDate" value={cd.signDate} mono />}
                  {cd.signTime && <Row label="signTime" value={cd.signTime} mono />}
                  {cd.autoFilledFields && cd.autoFilledFields.length > 0 && (
                    <Row label="autoFilled" value={cd.autoFilledFields.join(', ')} badge color="green" />
                  )}
                  {cd.missingRequiredFields && cd.missingRequiredFields.length > 0 && (
                    <Row label="missingFields" value={cd.missingRequiredFields.join(', ')} badge color="amber" />
                  )}
                  {cd.feeSource && <Row label="feeSource" value={cd.feeSource} mono />}
                  {cd.partySource && <Row label="partySource" value={cd.partySource} mono />}
                  {/* ── Governance 診斷 ── */}
                  {cd.customerType !== undefined && (
                    <Row label="customerType" value={cd.customerType ?? 'unknown'} badge color={cd.customerType === 'company' ? 'gray' : 'green'} />
                  )}
                  {cd.normalizedCustomerKeys && cd.normalizedCustomerKeys.length > 0 && (
                    <Row label="normalizedCustomerKeys" value={cd.normalizedCustomerKeys.join(', ')} badge color="green" />
                  )}
                  {cd.identityFieldSource && (
                    <Row label="identityFieldSource" value={cd.identityFieldSource} mono />
                  )}
                  {cd.normalizedIdentitySource !== undefined && (
                    <Row label="normalizedIdentitySource" value={cd.normalizedIdentitySource || 'missing'} badge
                      color={cd.normalizedIdentitySource === 'missing' ? 'amber' : 'green'} />
                  )}
                  {cd.verificationStatus && (
                    <Row label="verificationStatus" value={cd.verificationStatus} badge
                      color={cd.verificationStatus === 'verified' ? 'green' : cd.verificationStatus === 'missing' || cd.verificationStatus === 'gateway_error' ? 'red' : 'amber'} />
                  )}
                  {cd.ocrMismatchDetected !== undefined && (
                    <Row label="ocrMismatchDetected" value={cd.ocrMismatchDetected} badge color={cd.ocrMismatchDetected ? 'red' : 'green'} />
                  )}
                  {cd.guarantorExists !== undefined && (
                    <Row label="guarantorExists" value={cd.guarantorExists} badge color={cd.guarantorExists ? 'green' : 'amber'} />
                  )}
                  {cd.guarantorSource && (
                    <Row label="guarantorSource" value={cd.guarantorSource} mono />
                  )}
                  {cd.editableFields && (
                    <Row label="editableFields" value={cd.editableFields.length > 0 ? cd.editableFields.join(', ') : '(none)'} badge color={cd.editableFields.length > 0 ? 'amber' : 'gray'} />
                  )}
                  {/* ── Normalized mapping diagnostics ── */}
                  {cd.normalizedRentalStart && <Row label="normalizedRentalStart" value={cd.normalizedRentalStart} mono />}
                  {cd.normalizedRentalEnd && <Row label="normalizedRentalEnd" value={cd.normalizedRentalEnd} mono />}
                  {cd.normalizedRentAmount !== undefined && (
                    <Row label="normalizedRentAmount" value={cd.normalizedRentAmount !== null ? `NT$ ${cd.normalizedRentAmount?.toLocaleString()}` : '資料未填'} badge color={cd.normalizedRentAmount ? 'green' : 'amber'} />
                  )}
                  {cd.normalizedDepositAmount !== undefined && (
                    <Row label="normalizedDepositAmount" value={cd.normalizedDepositAmount !== null ? `NT$ ${cd.normalizedDepositAmount?.toLocaleString()}` : '資料未填'} badge color={cd.normalizedDepositAmount ? 'green' : 'amber'} />
                  )}
                  {cd.normalizedPromissoryAmount !== undefined && (
                    <Row label="normalizedPromissoryAmount" value={cd.normalizedPromissoryAmount > 0 ? `NT$ ${cd.normalizedPromissoryAmount.toLocaleString()}` : '資料未填'} badge color={cd.normalizedPromissoryAmount > 0 ? 'green' : 'amber'} />
                  )}
                  {cd.customerAddressSource && <Row label="customerAddressSource" value={cd.customerAddressSource} badge color={cd.customerAddressSource === 'missing' ? 'amber' : 'green'} />}
                  {cd.contractTextMappingStatus && (
                    <Row label="contractTextMappingStatus" value={cd.contractTextMappingStatus} badge color={cd.contractTextMappingStatus === 'full' ? 'green' : cd.contractTextMappingStatus === 'partial' ? 'amber' : 'gray'} />
                  )}
                  {/* ── Phase 1B Snapshot diagnostics ── */}
                  {cd.snapshotReadAttempted !== undefined && (
                    <Row label="snapshotReadAttempted" value={cd.snapshotReadAttempted} badge color={cd.snapshotReadAttempted ? 'green' : 'gray'} />
                  )}
                  {cd.snapshotStatus && (
                    <Row
                      label="snapshotStatus"
                      value={cd.snapshotStatus}
                      badge
                      color={
                        cd.snapshotStatus === 'active' ? 'green'
                          : cd.snapshotStatus === 'archived' ? 'gray'
                          : cd.snapshotStatus === 'error' ? 'red'
                          : 'amber'
                      }
                    />
                  )}
                  {cd.snapshotErrorCode && (
                    <Row label="snapshotErrorCode" value={cd.snapshotErrorCode} mono />
                  )}
                  {cd.snapshotFeatureGate && (
                    <Row label="snapshotFeatureGate" value={cd.snapshotFeatureGate} badge color="amber" />
                  )}
                  <Row
                    label="createSnapshotEnabled"
                    value={cd.createSnapshotEnabled ?? false}
                    badge
                    color={(cd.createSnapshotEnabled ?? false) ? 'green' : 'amber'}
                  />
                  <Row
                    label="fullscreenSigningReady"
                    value={cd.fullscreenSigningReady ?? false}
                    badge
                    color={(cd.fullscreenSigningReady ?? false) ? 'green' : 'red'}
                  />
                  {/* ── Ver1103 Company Settings diagnostics ── */}
                  {cd.companySettingsLoaded !== undefined && (
                    <Row
                      label="companySettingsLoaded"
                      value={cd.companySettingsLoaded}
                      badge
                      color={cd.companySettingsLoaded ? 'green' : 'red'}
                    />
                  )}
                  {cd.companySource && (
                    <Row
                      label="companySource"
                      value={cd.companySource}
                      badge
                      color={cd.companySource === 'db_table' ? 'green' : 'red'}
                    />
                  )}
                  {cd.companyRequiredFieldsComplete !== undefined && (
                    <Row
                      label="companyRequiredFieldsComplete"
                      value={cd.companyRequiredFieldsComplete}
                      badge
                      color={cd.companyRequiredFieldsComplete ? 'green' : 'red'}
                    />
                  )}
                  {cd.companyMissingFields && cd.companyMissingFields.length > 0 && (
                    <Row label="companyMissingFields" value={cd.companyMissingFields.join(', ')} badge color="red" />
                  )}
                  {/* ── Ver1104 Company Edit Mode & Save Status ── */}
                  {cd.companyEditMode !== undefined && (
                    <Row label="companyEditMode" value={cd.companyEditMode ? 'open' : 'closed'} badge color={cd.companyEditMode ? 'amber' : 'green'} />
                  )}
                  {cd.companySaveStatus && (
                    <Row label="companySaveStatus" value={cd.companySaveStatus} badge
                      color={cd.companySaveStatus === 'success' ? 'green' : cd.companySaveStatus === 'error' ? 'red' : cd.companySaveStatus === 'saving' ? 'amber' : 'gray'} />
                  )}
                  {/* ── Ver1097 Token Renderer diagnostics ── */}
                  <Row
                    label="tokenRendererEnabled"
                    value={cd.tokenRendererEnabled ?? false}
                    badge
                    color={(cd.tokenRendererEnabled ?? false) ? 'green' : 'gray'}
                  />
                  {cd.templateVersion !== undefined && (
                    <Row label="templateVersion" value={cd.templateVersion || 'unknown'} mono />
                  )}
                  {cd.tokenRenderStatus && (
                    <Row
                      label="tokenRenderStatus"
                      value={cd.tokenRenderStatus}
                      badge
                      color={
                        cd.tokenRenderStatus === 'ok' ? 'green'
                          : cd.tokenRenderStatus === 'has_missing' ? 'amber'
                          : cd.tokenRenderStatus === 'all_missing' ? 'red'
                          : cd.tokenRenderStatus === 'has_unknown' ? 'red'
                          : 'gray'
                      }
                    />
                  )}
                  {cd.tokenReplacedCount !== undefined && (
                    <Row label="tokenReplacedCount" value={cd.tokenReplacedCount} />
                  )}
                  {cd.missingTokens && cd.missingTokens.length > 0 && (
                    <Row label="missingTokens" value={cd.missingTokens.join(', ')} badge color="amber" />
                  )}
                  {cd.unknownTokens && cd.unknownTokens.length > 0 && (
                    <Row label="unknownTokens" value={cd.unknownTokens.join(', ')} badge color="red" />
                  )}
                  {cd.legacyCompatibilityMode !== undefined && (
                    <Row
                      label="legacyCompatibilityMode"
                      value={cd.legacyCompatibilityMode ?? false}
                      badge
                      color={(cd.legacyCompatibilityMode ?? false) ? 'amber' : 'green'}
                    />
                  )}
                  {/* ── Ver1101 Source-of-Truth diagnostics ── */}
                  {cd.templateSource && (
                    <Row
                      label="templateSource"
                      value={cd.templateSource}
                      badge
                      color={cd.templateSource === 'db' ? 'green' : 'amber'}
                    />
                  )}
                  {cd.tokenSchemaVersion && (
                    <Row label="tokenSchemaVersion" value={cd.tokenSchemaVersion} mono />
                  )}
                  {cd.templateValidationStatus && (
                    <Row
                      label="templateValidationStatus"
                      value={cd.templateValidationStatus}
                      badge
                      color={
                        cd.templateValidationStatus === 'ok' ? 'green'
                          : cd.templateValidationStatus === 'has_missing' ? 'amber'
                          : cd.templateValidationStatus === 'has_unknown' ? 'red'
                          : 'gray'
                      }
                    />
                  )}
                  {cd.missingRequiredTokens && cd.missingRequiredTokens.length > 0 && (
                    <Row label="missingRequiredTokens" value={cd.missingRequiredTokens.join(', ')} badge color="red" />
                  )}
                  {cd.usingLocalSeedFallback !== undefined && (
                    <Row
                      label="usingLocalSeedFallback"
                      value={cd.usingLocalSeedFallback}
                      badge
                      color={cd.usingLocalSeedFallback ? 'amber' : 'green'}
                    />
                  )}
                  {cd.officialSnapshotBlocked !== undefined && (
                    <Row
                      label="officialSnapshotBlocked"
                      value={cd.officialSnapshotBlocked}
                      badge
                      color={cd.officialSnapshotBlocked ? 'amber' : 'green'}
                    />
                  )}
                  {cd.usingStagingTemplate !== undefined && (
                    <Row
                      label="usingStagingTemplate"
                      value={cd.usingStagingTemplate ?? false}
                      badge
                      color={(cd.usingStagingTemplate ?? false) ? 'green' : 'gray'}
                    />
                  )}
                  <Row label="diagnosticTs" value={cd.timestamp ? new Date(cd.timestamp).toLocaleTimeString() : '—'} mono />
                </Section>
              );
            })()}

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                onClick={handleRefreshSession}
                className="px-2 py-1.5 rounded bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-medium cursor-pointer whitespace-nowrap"
              >
                Refresh session
              </button>
              <button
                onClick={handleClearAuthStorage}
                className="px-2 py-1.5 rounded bg-white border border-red-200 text-red-700 hover:bg-red-50 text-xs font-medium cursor-pointer whitespace-nowrap"
              >
                Clear auth storage
              </button>
              <button
                onClick={handleSignOutAndReload}
                className="px-2 py-1.5 rounded bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-medium cursor-pointer whitespace-nowrap"
              >
                Sign out &amp; reload
              </button>
              <button
                onClick={handleCopyReport}
                className="px-2 py-1.5 rounded bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 text-xs font-medium cursor-pointer whitespace-nowrap"
              >
                Copy debug report
              </button>
            </div>
          </div>

          {/* META */}
          <Section title="Meta" icon="ri-information-line">
            <Row label="Route" value={location.pathname} mono />
            <Row label="Locale" value={i18n.language} mono />
            <Row label="User role" value={role || '—'} />
            <Row label="ENV mode" value={import.meta.env.MODE} />
            <Row label="Version" value={import.meta.env.VITE_APP_VERSION ?? '—'} />
          </Section>

          {/* DATA */}
          {pageData && (
            <Section title="Data" icon="ri-database-2-line">
              {pageData.dataSource && (
                <Row
                  label="Source"
                  value={pageData.dataSource.label ?? pageData.dataSource.type}
                  badge
                  color="gray"
                />
              )}
              {pageData.loading !== undefined && (
                <Row
                  label="Loading"
                  value={pageData.loading ? 'true' : 'false'}
                  badge
                  color={pageData.loading ? 'amber' : 'green'}
                />
              )}
              {pageData.error !== undefined && (
                <Row
                  label="Error"
                  value={pageData.error ?? 'none'}
                  badge
                  color={pageData.error ? 'red' : 'green'}
                />
              )}
              {pageData.isEmpty !== undefined && (
                <Row
                  label="Empty"
                  value={pageData.isEmpty ? 'true' : 'false'}
                  badge
                  color={pageData.isEmpty ? 'amber' : 'green'}
                />
              )}
              {pageData.responseSummary && (
                <div className="mt-1 p-2 bg-gray-50 rounded text-xs font-mono text-gray-600 break-all whitespace-pre-wrap">
                  {JSON.stringify(redactSensitiveData(pageData.responseSummary), null, 2)}
                </div>
              )}
            </Section>
          )}

          {/* I18N */}
          {pageData?.i18n && (
            <Section title="i18n / Fallback" icon="ri-translate-2">
              <Row label="Requested" value={pageData.i18n.requestedLocale} mono />
              <Row label="Displayed" value={pageData.i18n.displayedLocale} mono />
              <Row label="Fallback chain" value={pageData.i18n.fallbackChain.join(' → ')} mono />
              {(pageData.i18n.missingEnFields ?? []).length > 0 && (
                <div>
                  <div className="text-amber-600 font-medium mt-1">Missing _en fields:</div>
                  {(pageData.i18n.missingEnFields ?? []).map((f) => (
                    <div key={f} className="text-red-600 pl-2">
                      {f}
                    </div>
                  ))}
                </div>
              )}
              {(pageData.i18n.missingJaFields ?? []).length > 0 && (
                <div>
                  <div className="text-amber-600 font-medium mt-1">Missing _ja fields:</div>
                  {(pageData.i18n.missingJaFields ?? []).map((f) => (
                    <div key={f} className="text-red-600 pl-2">
                      {f}
                    </div>
                  ))}
                </div>
              )}
              {(pageData.i18n.missingZhFields ?? []).length > 0 && (
                <div>
                  <div className="text-amber-600 font-medium mt-1">Missing _zh fields:</div>
                  {(pageData.i18n.missingZhFields ?? []).map((f) => (
                    <div key={f} className="text-red-600 pl-2">
                      {f}
                    </div>
                  ))}
                </div>
              )}
              {(pageData.i18n.trilingualFields ?? []).length > 0 && (
                <div className="mt-2">
                  <div className="text-gray-600 font-medium mb-1 flex items-center gap-1">
                    Tri-lingual fields
                    <span className="text-gray-400 font-normal">(zh / ja / en | payload)</span>
                  </div>
                  <div className="space-y-0.5 bg-gray-50 rounded p-2">
                    {(pageData.i18n.trilingualFields ?? []).map((f) => (
                      <TrilingualFieldRow key={f.field} field={f} />
                    ))}
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1.5 text-xs">
                    <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700">
                      enabled = in payload
                    </span>
                    <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                      omitted = schema pending
                    </span>
                    <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                      disabled = off
                    </span>
                  </div>
                </div>
              )}
              {(pageData.i18n.missingEnFields ?? []).length === 0 &&
                (pageData.i18n.missingJaFields ?? []).length === 0 &&
                (pageData.i18n.missingZhFields ?? []).length === 0 &&
                (pageData.i18n.trilingualFields ?? []).length === 0 && (
                  <div className="text-green-600 text-xs mt-1">All tri-lingual fields present</div>
                )}
            </Section>
          )}

          {/* TAGS */}
          {pageData?.tags && (
            <Section title="display_tags" icon="ri-price-tag-3-line">
              <div className="text-gray-600 mb-1">raw:</div>
              {rawTags.length === 0 ? (
                <div className="text-gray-400 pl-2 italic">none</div>
              ) : (
                rawTags.map((t) => {
                  const { canonical, legacy } = analyzeTag(t);
                  return (
                    <div key={t} className="flex items-center gap-1.5 pl-2">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          canonical ? 'bg-green-500' : legacy ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                      ></span>
                      <span className="font-mono text-gray-700">{t}</span>
                      {legacy && <span className="text-amber-600">(legacy)</span>}
                      {!canonical && !legacy && <span className="text-red-600">(illegal)</span>}
                    </div>
                  );
                })
              )}
              <div className="text-gray-600 mt-2 mb-1">normalized:</div>
              {normalizedTags.length === 0 ? (
                <div className="text-gray-400 pl-2 italic">none</div>
              ) : (
                normalizedTags.map((t) => (
                  <div key={t} className="flex items-center gap-1.5 pl-2">
                    <span className="w-2 h-2 rounded-full shrink-0 bg-green-500"></span>
                    <span className="font-mono text-gray-700">{t}</span>
                  </div>
                ))
              )}
              <div className="flex gap-2 mt-2">
                <span
                  className={`px-1.5 py-0.5 rounded text-xs ${
                    hasLegacy ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  legacy: {hasLegacy ? 'yes' : 'no'}
                </span>
                <span
                  className={`px-1.5 py-0.5 rounded text-xs ${
                    hasIllegal ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  illegal: {hasIllegal ? 'yes' : 'no'}
                </span>
              </div>
            </Section>
          )}

          {/* SORT DEBUG (owner-only) */}
          {pageData?.responseSummary && (
            <Section title="Sort Debug" icon="ri-sort-desc">
              <Row label="sortBy" value={String(pageData.responseSummary.sort_by ?? '—')} mono />
              <Row label="sort label" value={String(pageData.responseSummary.sort_mode_label ?? '—')} />
              {Array.isArray(pageData.responseSummary.top10_brand_sort_debug) && (
                <div className="mt-1">
                  <div className="text-gray-600 mb-1 text-[10px] uppercase tracking-wider">Top 10 brand sort keys</div>
                  <div className="space-y-0.5 bg-gray-50 rounded p-2">
                    {pageData.responseSummary.top10_brand_sort_debug.map((item: any, idx: number) => (
                      <div key={idx} className="flex items-center gap-2 text-xs">
                        <span className="text-gray-400 w-4 shrink-0">{idx + 1}</span>
                        <span className="font-mono text-gray-700 truncate max-w-[120px]">{item.brand_display_name ?? item.brand}</span>
                        <span className="text-gray-400">·</span>
                        <span className="text-gray-500 truncate">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          <div className="mt-3 pt-3 border-t border-gray-100 text-gray-400 text-xs space-y-1">
            <div className="text-center">
              This panel is visible to <strong>owner</strong> only.
            </div>
          </div>
        </div>
      )}
    </>
  );
}